/**
 *
 * Copyright 2020-2026 Splunk Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

import { Attributes } from '@opentelemetry/api'
import { Span, SpanProcessor } from '@opentelemetry/sdk-trace-base'

import { SESSION_DURATION_MS, SessionManager } from './managers'
import { UserTrackingMode } from './types/config'
import { forgetAnonymousId, getOrCreateAnonymousId } from './user-tracking'

export class SplunkSpanAttributesProcessor implements SpanProcessor {
	private readonly _globalAttributes: Attributes

	constructor(
		private sessionManager: SessionManager,
		globalAttributes: Attributes,
		private useLocalStorageForSessionMetadata: boolean,
		private getUserTracking: () => UserTrackingMode,
		private domain?: string,
	) {
		this._globalAttributes = globalAttributes ?? {}
	}

	forceFlush(): Promise<void> {
		return Promise.resolve()
	}

	getGlobalAttributes(): Attributes {
		return this._globalAttributes
	}

	onEnd(span: Span): void {
		const spanEndTimeSeconds = span.endTime[0]
		const sessionId = (span.attributes['splunk.rumSessionId'] as string | undefined) ?? ''
		const sessionState = this.sessionManager.sessionHistory.get(sessionId)
		if (sessionState) {
			const sessionMaxDurationTimeInSeconds = (sessionState.startTime + SESSION_DURATION_MS) / 1000
			// If there is a long span for example fetch that takes hours and the session expires, we need to truncate the span
			// to the session expiration time
			if (spanEndTimeSeconds > sessionMaxDurationTimeInSeconds) {
				span.endTime[0] = sessionMaxDurationTimeInSeconds
				span.endTime[1] = 0
				span.duration[0] = sessionMaxDurationTimeInSeconds - span.startTime[0]
				span.duration[1] = 0
			}
		}
	}

	onStart(span: Span): void {
		span.setAttribute('location.href', location.href)
		span.setAttributes(this._globalAttributes)

		const sessionState = this.sessionManager.getSessionState()

		if (sessionState.state !== 'expired-duration') {
			span.setAttribute('splunk.rumSessionId', sessionState.id)
		}

		if (this.getUserTracking() === 'anonymousTracking') {
			span.setAttribute(
				'user.anonymous_id',
				getOrCreateAnonymousId({
					domain: this.domain,
					useLocalStorage: this.useLocalStorageForSessionMetadata,
				}),
			)
		}

		span.setAttribute('browser.instance.visibility_state', document.visibilityState)

		// Remove 'unknown_service' service.name from resource
		if (span.resource.attributes['service.name'] === 'unknown_service') {
			delete span.resource.attributes['service.name']
		}
	}

	setGlobalAttributes(attributes?: Attributes): void {
		if (attributes) {
			Object.assign(this._globalAttributes, attributes)
		} else {
			for (const key of Object.keys(this._globalAttributes)) {
				delete this._globalAttributes[key]
			}
		}
	}

	shutdown(): Promise<void> {
		forgetAnonymousId()
		return Promise.resolve()
	}
}
